/**
 * Fluxible actions for use in the add flow.
 */
import type Q from 'q';

import type { ActionContext } from 'js/lib/ActionContext';

import { redesignStaffManagement } from 'bundles/staff-management/src/utils/epic';

import _t from 'i18n!nls/staff-management';

import type { NotificationParam } from '../../common/notifications/Notifications';
import { EVENT_NAMES } from '../constants';
import { StaffManagementStoreV2 } from '../store';
import type { EmailableInterface, StaffMembershipRoleInterface, StaffMembershipType } from '../types';
import { create } from './core';
import * as inviteFlow from './inviteFlow';

/**
 * Prompt user to create memberships for users.
 *
 * Calling with no args acts as an unset.
 *
 * This will trigger a modal in the flow.
 */
export const prompt = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  {
    show = false,
  }: {
    show?: boolean;
  } = {}
): void => {
  // Also remove any saved users on show == false
  const updatedState: { show: boolean; users?: Array<EmailableInterface> } = { show };
  if (!show) {
    updatedState.users = [];
  }
  actionContext.dispatch(EVENT_NAMES.UI, { addFlow: updatedState });
};

/**
 * Add a set of memberships to a set of users, and handoff any non-courserians to
 * the invite flow.abnf
 */
export const confirm = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  /** List of users from which to add memberships to, and the type of memberships. */
  {
    fromId,
    roles,
    type,
    users,
    notify,
    // API DI.
    _create = create,
  }: {
    fromId: string | number;
    roles: Array<string>;
    type: keyof typeof StaffMembershipType;
    users: Array<EmailableInterface>;
    _create?(
      actionContext: ActionContext,
      payload: {
        fromId: string;
        roles: Array<string>;
        type: keyof typeof StaffMembershipType;
        users: Array<EmailableInterface>;
      }
    ): Q.Promise<Array<Array<string>>>;
    notify: (notification: NotificationParam) => void;
  }
) => {
  const addFlowState = actionContext.getStore(StaffManagementStoreV2).ui('addFlow');
  const resolvedFromId = fromId || addFlowState.fromId;
  const resolvedRoles = roles || addFlowState.roles;
  const resolvedType = type || addFlowState.type;
  const resolvedUsers = users || addFlowState.users;

  // Close the modal once we've gotten the state information we need.
  actionContext.executeAction(prompt);
  return actionContext
    .executeAction<ReturnType<typeof _create>, Parameters<typeof _create>[1]>(_create, {
      fromId: resolvedFromId,
      roles: resolvedRoles,
      type: resolvedType,
      users: resolvedUsers,
    })
    .then(([existingUserEmails, newUserEmails]) => {
      // If we have any existing users that we added memberships to, notify this happened.
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (existingUserEmails.length) {
        notify({ message: _t('Roles created successfully.'), type: 'success' });
      }

      // If we have any new users, hand these and the additional information over to the invite flow.
      // In the accelerated flow the invites will be sent automatically, so skipping this step.
      // @ts-expect-error TS18048 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      if (newUserEmails.length && !redesignStaffManagement()) {
        actionContext.executeAction(inviteFlow.prompt, {
          fromId: resolvedFromId,
          roles: resolvedRoles,
          type: resolvedType,
          users: newUserEmails,
        });
      }
    })
    .catch(() => {
      notify({
        message: _t('Error during membership creation. Recommend refreshing your browser and trying again.'),
        type: 'error',
      });
    });
};

/** Set of roles we will potentially add to a set of users. */
export const roles = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  roles: Array<StaffMembershipRoleInterface> = [] // eslint-disable-line @typescript-eslint/no-shadow
): void => {
  const addFlowState = actionContext.getStore('StaffManagementStoreV2').ui('addFlow');
  actionContext.dispatch(EVENT_NAMES.UI, { addFlow: { ...addFlowState, roles } });
};

/** Set the user emails we will potentially add to a set of roles. */
export const users = (
  /** Provided by fluxible. */
  actionContext: ActionContext,
  users: Array<EmailableInterface> = [] // eslint-disable-line @typescript-eslint/no-shadow
): void => {
  const addFlowState = actionContext.getStore('StaffManagementStoreV2').ui('addFlow');
  actionContext.dispatch(EVENT_NAMES.UI, { addFlow: { ...addFlowState, users } });
};
